/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';

type TComponentSize = 'small' | 'medium' | 'large';

interface IProps {
  totalSteps: number;
  currentStep: number;
  theme?: ETheme;
  size?: TComponentSize;
  width?: '100%' | 'fit-content' | string;
  height?: '100%' | 'auto' | string;
}

const createDesign = (componentSize: TComponentSize, theme: ETheme) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (componentSize) {
      case 'small':
        return { dotSize: '6px', gap: '6px' };
      case 'large':
        return { dotSize: '10px', gap: '10px' };
      case 'medium':
      default:
        return { dotSize: '8px', gap: '8px' };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.BLACK:
        return { activeColor: color.BLACK_2, completedColor: color.GRAY_1, defaultColor: color.GRAY_2 };
      case ETheme.GRAY:
        return { activeColor: color.GRAY_1, completedColor: color.GRAY_2, defaultColor: color.GRAY_3 };
      case ETheme.BROWN:
      default:
        return { activeColor: color.BROWN_1, completedColor: color.BROWN_2, defaultColor: color.GRAY_2 };
    }
  };

  const sizeDesign = createSize();
  const themeDesign = createTheme();

  return {
    ...sizeDesign,
    ...themeDesign,
  };
};

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
  $width: string;
  $height: string;
}

const Wrapper = styled.div<IWrapperProps>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.$design.gap};
  width: ${(props) => props.$width};
  height: ${(props) => props.$height};
`;

interface IDotProps {
  $dotSize: string;
  $color: string;
}

const Dot = styled.div<IDotProps>`
  width: ${(props) => props.$dotSize};
  height: ${(props) => props.$dotSize};
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  transition: background-color 0.3s ease;
`;

const StepIndicator: React.FC<IProps> = ({
  totalSteps,
  currentStep,
  theme = ETheme.BROWN,
  size = 'medium',
  width = 'fit-content',
  height = 'auto',
}) => {
  if (currentStep > totalSteps) return null;

  const design = createDesign(size, theme);

  return (
    <Wrapper $design={design} $width={width} $height={height}>
      {Array.from({ length: totalSteps }, (item, idx) => idx + 1).map((s) => {
        const dotColor =
          s === currentStep ? design.activeColor : s < currentStep ? design.completedColor : design.defaultColor;

        return <Dot key={s} $dotSize={design.dotSize} $color={dotColor} />;
      })}
    </Wrapper>
  );
};

export default StepIndicator;
